import { useEffect, useState } from 'react';
import { Caravan, Plus, Trash2, Loader2, ShieldAlert, ShieldCheck, X } from 'lucide-react';
import * as api from '../../lib/api';
import { VEHICLE_KINDS, vehicleKindMeta } from '../../lib/fit';
import { formatDate, formatDimensions } from '../../lib/format';

const INSURANCE_STYLES = {
  current: { Icon: ShieldCheck, className: 'text-emerald-700', label: 'Insured' },
  expiring_soon: { Icon: ShieldAlert, className: 'text-amber-700', label: 'Expiring soon' },
  expired: { Icon: ShieldAlert, className: 'text-rose-700', label: 'Insurance expired' },
  missing: { Icon: ShieldAlert, className: 'text-slate-500', label: 'No insurance on file' },
};

export default function VehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = async () => {
    setState('loading');
    try {
      const { vehicles: list } = await api.listVehicles();
      setVehicles(list);
      setState('ready');
    } catch (err) {
      setError(err.message);
      setState('error');
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (vehicle) => {
    setError(null);
    try {
      await api.deleteVehicle(vehicle.public_id);
      await load();
    } catch (err) {
      // The API refuses to delete a vehicle on a live lease; show why.
      setError(err.message);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Your vehicles</h1>
          <p className="mt-1 text-sm text-slate-600">
            Dimensions decide which spaces you can rent, so keep them accurate.
          </p>
        </div>
        <button type="button" onClick={() => setFormOpen(true)} className="btn-primary shrink-0">
          <Plus className="h-4 w-4" aria-hidden="true" /> Add vehicle
        </button>
      </div>

      {error && (
        <p role="alert" className="mt-4 rounded-lg bg-rose-50 p-3 text-sm font-medium text-rose-800">{error}</p>
      )}

      {state === 'loading' && (
        <div className="mt-8 grid place-items-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" />
        </div>
      )}

      {state === 'ready' && vehicles.length === 0 && (
        <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <Caravan className="mx-auto h-8 w-8 text-brand-300" aria-hidden="true" />
          <p className="mt-3 text-sm font-bold text-slate-900">No vehicles yet</p>
          <p className="mt-1 text-xs text-slate-500">Add one and every space gets graded against it.</p>
        </div>
      )}

      <ul className="mt-6 space-y-3">
        {vehicles.map((vehicle) => {
          const insurance = INSURANCE_STYLES[vehicle.insurance_status] ?? INSURANCE_STYLES.missing;
          const locked = vehicle.active_lease_count > 0;
          return (
            <li key={vehicle.public_id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-extrabold text-slate-900">
                    {vehicle.label || vehicleKindMeta(vehicle.kind).label}
                    <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-600">
                      {vehicle.kind.replace(/_/g, ' ')}
                    </span>
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') || '—'}
                    {vehicle.license_plate && ` · ${vehicle.license_plate} ${vehicle.plate_state ?? ''}`}
                  </p>
                  <p className="mt-2 text-xs font-semibold text-slate-700">
                    {formatDimensions(vehicle.length_ft, vehicle.width_ft)}
                    {vehicle.height_ft && ` × ${vehicle.height_ft}ft tall`}
                    {vehicle.slide_outs > 0 && ` · ${vehicle.slide_outs} slide-out${vehicle.slide_outs === 1 ? '' : 's'}`}
                  </p>
                  <p className={`mt-1.5 inline-flex items-center gap-1 text-[12px] font-semibold ${insurance.className}`}>
                    <insurance.Icon className="h-3.5 w-3.5" aria-hidden="true" />
                    {insurance.label}
                    {vehicle.insurance_expires_on && ` — expires ${formatDate(vehicle.insurance_expires_on)}`}
                  </p>
                  {locked && (
                    <p className="mt-1 text-[12px] text-slate-500">
                      On {vehicle.active_lease_count} active lease{vehicle.active_lease_count === 1 ? '' : 's'}
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => remove(vehicle)}
                  disabled={locked}
                  title={locked ? 'End the lease before removing this vehicle' : 'Remove vehicle'}
                  aria-label={`Remove ${vehicle.label ?? 'vehicle'}`}
                  className="shrink-0 rounded-lg p-2 text-slate-400 transition hover:bg-rose-50 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {formOpen && (
        <VehicleForm
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); load(); }}
          onError={setError}
        />
      )}
    </div>
  );
}

function VehicleForm({ onClose, onSaved, onError }) {
  const [form, setForm] = useState(() => {
    const preset = vehicleKindMeta('travel_trailer');
    return {
      kind: 'travel_trailer', label: '', year: '', make: '', model: '',
      length_ft: preset.length, width_ft: preset.width, height_ft: preset.height,
      slide_outs: 0, license_plate: '', plate_state: '', insurance_expires_on: '',
    };
  });
  const [saving, setSaving] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  /** Changing type reseeds dimensions, which the user can then adjust. */
  const setKind = (kind) => {
    const preset = vehicleKindMeta(kind);
    setForm({ ...form, kind, length_ft: preset.length, width_ft: preset.width, height_ft: preset.height });
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.createVehicle({
        ...form,
        year: form.year === '' ? null : Number(form.year),
        length_ft: Number(form.length_ft),
        width_ft: Number(form.width_ft),
        height_ft: form.height_ft === '' ? null : Number(form.height_ft),
        slide_outs: Number(form.slide_outs),
        insurance_expires_on: form.insurance_expires_on || null,
      });
      onSaved();
    } catch (err) {
      onError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4" role="dialog" aria-modal="true" aria-label="Add vehicle">
      <form onSubmit={submit} className="max-h-full w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-extrabold text-slate-900">Add a vehicle</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <label className="field-label mb-1" htmlFor="v-kind">Type</label>
        <select id="v-kind" className="field-input mb-4" value={form.kind} onChange={(e) => setKind(e.target.value)}>
          {VEHICLE_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>

        <label className="field-label mb-1" htmlFor="v-label">Nickname (optional)</label>
        <input id="v-label" className="field-input mb-4" value={form.label} onChange={set('label')} placeholder="The Big One" />

        <div className="mb-4 grid grid-cols-3 gap-3">
          <Field id="v-year" label="Year" type="number" value={form.year} onChange={set('year')} />
          <Field id="v-make" label="Make" value={form.make} onChange={set('make')} />
          <Field id="v-model" label="Model" value={form.model} onChange={set('model')} />
        </div>

        <p className="field-label mb-1">Dimensions — these decide what fits</p>
        <div className="mb-4 grid grid-cols-4 gap-3">
          <Field id="v-len" label="Length ft" type="number" step="0.5" required value={form.length_ft} onChange={set('length_ft')} />
          <Field id="v-wid" label="Width ft" type="number" step="0.5" value={form.width_ft} onChange={set('width_ft')} />
          <Field id="v-hei" label="Height ft" type="number" step="0.5" value={form.height_ft} onChange={set('height_ft')} />
          <Field id="v-slides" label="Slides" type="number" value={form.slide_outs} onChange={set('slide_outs')} />
        </div>

        <div className="mb-4 grid grid-cols-3 gap-3">
          <Field id="v-plate" label="Plate" value={form.license_plate} onChange={set('license_plate')} />
          <Field id="v-state" label="State" maxLength={2} value={form.plate_state} onChange={set('plate_state')} />
          <Field id="v-ins" label="Insurance expires" type="date" value={form.insurance_expires_on} onChange={set('insurance_expires_on')} />
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary flex-1">
            {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {saving ? 'Saving…' : 'Save vehicle'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ id, label, ...props }) {
  return (
    <div>
      <label className="field-label mb-1" htmlFor={id}>{label}</label>
      <input id={id} className="field-input" {...props} />
    </div>
  );
}
